import React, { createContext, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import usePipelineQuery from '../hooks/usePipelineQuery'
import { useParameterReplacementEx } from '../../../../hooks/useAbundanceEngineParameters'
import { useVariables } from '../../../../providers/VariableProvider'
import useRegisterDDPForInspector from '../inspector/useRegisterDDPForInspector'

export const PipelineDataContext = createContext({ data: [], isLoading: true, isFetching: false, error: null, inspectorId: null })
const DEFAULT_VARIABLE_BINDING = {
  asOfDate: '$asOfDate',
  quickDate: '$quickDate',
  levelIds: ['$clientId'],
  levelTypeId: 201
}

export const usePipelineDataContextWithVariables = (pipeline, variableBindings) => {
  const { variables } = useVariables()
  const [_substituted, missing] = useParameterReplacementEx(variableBindings || DEFAULT_VARIABLE_BINDING, variables)

  const pd = usePipelineQuery(pipeline, _substituted, !!missing.length)
  const cd = useContext(PipelineDataContext)

  // Mirror `usePipelineQuery` exactly so the inspector's debug trace matches
  // what `execute-data-pipeline` was sent: stored pipelineCodes always get
  // `{ filters: ... }`, with `_substituted.filters` unwrapped if present.
  const inspectorVariables = useMemo(() => {
    if (typeof pipeline !== 'string') return _substituted
    return {
      filters: (_substituted && 'filters' in _substituted) ? _substituted.filters : _substituted
    }
  }, [pipeline, _substituted])

  const ownInspectorId = useRegisterDDPForInspector({
    // Skip registration when missing variables would disable the live query —
    // the page didn't fetch, so the inspector shouldn't either.
    pipeline: (pd.enabled && !missing.length) ? pipeline : undefined,
    variables: inspectorVariables,
    isLoading: pd.isLoading,
    error: pd.error
  })

  return useMemo(() => {
    if (pd.enabled) return { ...pd, inspectorId: ownInspectorId }
    return cd
  }, [pd, cd, ownInspectorId])
}

function DDPDataProvider ({ pipeline, variables, children }) {
  const value = usePipelineDataContextWithVariables(pipeline, variables)

  return (
    <PipelineDataContext.Provider value={value}>
      {children}
    </PipelineDataContext.Provider>
  )
}

DDPDataProvider.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  /** substitution values */
  variables: PropTypes.object,

  children: PropTypes.node
}

DDPDataProvider.defaultProps = {
}

export default DDPDataProvider
